import { useAppMode } from '@carlito/hooks';
import { IonAlert, IonButton } from '@ionic/react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IoAlert } from 'react-icons/io5';
import { BookingDatePicker } from './components/BookingDatePicker';
import BookingPriceVariants from './components/BookingPriceVariants';
import { BookingSlotPicker } from './components/BookingSlotPicker/BookingSlotPicker';
import BookingSteps from './components/BookingSteps';
import { BookitoParticipantForm } from './components/BookitoParticipantForm';
import {
  BookitoGuestChip,
  BookitoParticipantsPicker,
} from './components/BookitoParticipantsPicker';
import GuestAppBookButton from './components/GuestAppBookButton';
import { useBookingFormHandler } from './hooks/useBookingFormHandler';
import { BookingStepViews } from './shared/constants';
import { BookitoBookingFormProps } from './shared/types';
import ExtraWishesInput from './components/ExtraWishesInput';

export function BookitoEventBookingForm(props: BookitoBookingFormProps) {
  const {
    hotelId,
    event,
    isTimeSlotEnabled,
    availableOnDates,
    activeLanguage,
    renderQuantityControls,
    renderSubmitButton = GuestAppBookButton,
    shouldHideGuestDetailsForm = false,
    isDisabled = false,
    renderGuestChip = BookitoGuestChip,
    renderActionButton,
    renderError,
    renderLoader,
    formStore,
  } = props;

  const {
    state: {
      isSubmitting,
      isSuccess,
      isEditingCarlitoHubUserBooking,
      allParticipants = [],
      hasPmsEnabled,
      isEditMode,
      fieldError,
      steps,
      currentStep,
      isBookingRefEnabled,
      isRoomNrEnabled,
    },
    actions,
  } = formStore;

  const { t } = useTranslation();

  const {
    state: {
      values,
      willBePutOnWaitingList,
      bookBtnContent,
      isLoadingGuest,
      isGuestInformationInvalid,
      isGuestFormStepValid,
      maxNumberOfAdditionalParticipants,
    },
    actions: { resetError, onUpdateFormValues, onSubmitGuestForm, goToNextStep },
  } = useBookingFormHandler(props);

  const appMode = useAppMode();
  const isKioskMode = appMode === 'kiosk';

  const handleSubmit = () => {
    if (isTimeSlotEnabled && willBePutOnWaitingList) {
      actions.setFieldError(t('guest-app-too-many-participant-error'));
    } else {
      props.handleSubmit();
    }
  };

  function shouldRenderGuestForm() {
    return (
      (isEditMode && !isEditingCarlitoHubUserBooking) ||
      (!isEditMode && !hasPmsEnabled && !shouldHideGuestDetailsForm)
    );
  }

  const renderAdditionalParticipantSlot = () => {
    return (
      <>
        {renderQuantityControls &&
          renderQuantityControls({
            maxNumberOfAdditionalParticipants,
            count: values.numOfAdditionalParticipants,
            onCountChange: (numOfAdditionalParticipants) => {
              onUpdateFormValues({
                numOfAdditionalParticipants,
              });
            },
          })}
      </>
    );
  };

  const isSubmitButtonDisabled =
    isGuestInformationInvalid ||
    isDisabled ||
    !values.participants ||
    values.participants.length === 0 ||
    Boolean(isTimeSlotEnabled && willBePutOnWaitingList);

  return (
    <div className="space-y-4 relative">
      {!isEditMode && (
        <BookingSteps
          isCompleted={isSubmitting || isSuccess}
          isGuestFormStepValid={isGuestFormStepValid}
          bookingParams={values}
          stepState={currentStep}
          userLanguage={activeLanguage}
          steps={steps}
          updateStepState={actions.setCurrenStep}
          updateBookingParams={onUpdateFormValues}
        />
      )}

      {renderError ? (
        renderError({ fieldError, reset: resetError })
      ) : (
        <IonAlert
          isOpen={Boolean(fieldError)}
          onDidDismiss={resetError}
          header={t('error')}
          message={fieldError}
          buttons={['OK']}
        />
      )}
      {renderLoader && renderLoader({ isLoading: isLoadingGuest })}

      {currentStep === BookingStepViews.Calendar && (
        <BookingDatePicker
          onDateSelected={(selectedDate: Date) => {
            onUpdateFormValues({
              bookedForDate: selectedDate,
            });
            goToNextStep();
          }}
          selectedDate={values.bookedForDate}
          availableOnDates={availableOnDates}
        />
      )}

      {currentStep === BookingStepViews.SlotPicker && (
        <BookingSlotPicker
          maxParticipants={event.maxParticipants}
          bookingParams={values}
          ownerAuthId={hotelId}
          event={event}
          onSelectTimeSlot={(selectedTimeSlot) => {
            onUpdateFormValues({
              bookedForDate: new Date(selectedTimeSlot.startDateTime),
              selectedTimeSlot,
            });
            goToNextStep();
          }}
        />
      )}

      {/*render 3rd step with a guest form: when pms is enabled*/}
      {currentStep === BookingStepViews.GuestForm && (
        <div className={'flex flex-col items-center gap-y-3 w-full'}>
          <h1 className="text-xl font-bold text-center">
            <Trans i18nKey="guest-details" />{' '}
          </h1>
          <BookitoParticipantForm
            isRoomNrEnabled={isRoomNrEnabled}
            isBookingRefEnabled={isBookingRefEnabled}
            onValuesChange={() => {
              actions.setIsGuestLoggedIn(false);
              actions.setAllParticipants([]);
            }}
            isEditMode={isEditMode}
            hasPms={hasPmsEnabled}
            isKioskMode={isKioskMode}
          />
          {renderActionButton ? (
            renderActionButton({
              isDisabled: isGuestInformationInvalid,
              isLoading: isLoadingGuest,
              buttonText: t('next'),
              onClick: () => {
                onSubmitGuestForm();
              },
            })
          ) : (
            <IonButton
              disabled={isGuestInformationInvalid}
              color={'primary'}
              expand="block"
              fill="solid"
              shape="round"
              onClick={() => {
                onSubmitGuestForm();
              }}
            >
              <Trans>next</Trans>
            </IonButton>
          )}
        </div>
      )}

      {(currentStep === BookingStepViews.GuestExtra || currentStep === BookingStepViews.Extra) && (
        <div className={'space-y-4'}>
          <h1 className="text-xl font-bold text-center">
            <Trans i18nKey="guests-and-extras"></Trans>{' '}
          </h1>
          {shouldRenderGuestForm() && (
            <React.Fragment>
              <h1 className="text-md font-bold">
                <Trans i18nKey="guest-details"></Trans>{' '}
              </h1>
              <BookitoParticipantForm
                isRoomNrEnabled={isRoomNrEnabled}
                isBookingRefEnabled={isBookingRefEnabled}
                isEditMode={isEditMode}
                hasPms={false}
                isKioskMode={isKioskMode}
              />
            </React.Fragment>
          )}

          {hasPmsEnabled && allParticipants.length > 0 ? (
            <BookitoParticipantsPicker
              additionalParticipantSlot={renderAdditionalParticipantSlot()}
              participants={allParticipants}
              selectedParticipants={
                values.participants ? values.participants?.map((p) => p.carlitoHubUserId ?? '') : []
              }
              onParticipantsChange={(newIds) => {
                onUpdateFormValues({
                  participants: allParticipants
                    .filter((el) => newIds.includes(el.id))
                    .map((el) => ({
                      carlitoHubUserId: el.id,
                      roomNr: el.roomNr,
                      ...values.participants?.find((p) => p.carlitoHubUserId === el.id),
                      isMainBooker: el.isMainBooker,
                    })),
                });
              }}
              renderGuestChip={renderGuestChip}
            />
          ) : (
            renderAdditionalParticipantSlot()
          )}

          <div>
            {maxNumberOfAdditionalParticipants
              ? values.numOfAdditionalParticipants > maxNumberOfAdditionalParticipants && (
                  <div className={'flex justify-center w-full'}>
                    <div
                      className={
                        'flex items-center font-medium gap-x-1 text-xs text-gray-600 bg-red-100 text-center px-2 py-1 rounded-md'
                      }
                    >
                      <span
                        className={
                          'bg-red-400 flex items-center justify-center text-white rounded-full w-4 h-4'
                        }
                      >
                        <IoAlert />
                      </span>
                      <span> {t('guest-app-too-many-participant-error')}</span>
                    </div>
                  </div>
                )
              : null}
          </div>

          <BookingPriceVariants
            eventPriceVariants={event.priceVariants}
            priceVariants={values.priceVariants}
            onPriceVariantsChange={(priceVariants) => onUpdateFormValues({ priceVariants })}
          />
          <ExtraWishesInput enabledKeyboard={isKioskMode} />
        </div>
      )}
      {currentStep === steps.at(-1) &&
        renderSubmitButton({
          handleSubmit: handleSubmit,
          isTimeSlotEnabled,
          isDisabled: isSubmitButtonDisabled,
          isSubmitting,
          bookBtnContent,
          willBePutOnWaitingList,
        })}
    </div>
  );
}
